import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { History, Search, RefreshCw, Loader2 } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";
import { toast } from "sonner";

// Human-friendly labels + accent colors per event type. Unknown types fall
// back to a neutral gray.
const EVENT_META = {
  created:              { label: "Created",                bg: "#eef2ff", fg: "#3730a3" },
  billing_edit:         { label: "Billing edit",           bg: "#f0f9ff", fg: "#075985" },
  billing_edit_after_delivery: { label: "Billing edit (post-delivery)", bg: "#fef3c7", fg: "#78350f" },
  assigned:             { label: "Assigned to route",      bg: "#e0f2fe", fg: "#075985" },
  on_the_way:           { label: "On the way",             bg: "#dbeafe", fg: "#1e3a8a" },
  delivered:            { label: "Delivered",              bg: "#dcfce7", fg: "#166534" },
  failed:               { label: "Failed",                 bg: "#fee2e2", fg: "#b91c1c" },
  returned:             { label: "Returned to pharmacy",   bg: "#ede9fe", fg: "#5b21b6" },
  retry_scheduled:      { label: "Retry scheduled",        bg: "#fef3c7", fg: "#78350f" },
  restricted_hold:      { label: "Restricted ZIP hold",    bg: "#fee2e2", fg: "#b91c1c" },
  restricted_approved:  { label: "Restricted ZIP approved", bg: "#dcfce7", fg: "#166534" },
  restricted_rejected:  { label: "Restricted ZIP rejected", bg: "#fee2e2", fg: "#7f1d1d" },
  pod_correction:       { label: "POD corrected",          bg: "#f0f9ff", fg: "#075985" },
  received_from_pharmacy: { label: "Received from pharmacy", bg: "#e0f2fe", fg: "#0369a1" },
  address_updated:      { label: "Address updated",        bg: "#f0f9ff", fg: "#075985" },
  price_recomputed:     { label: "Price recomputed",       bg: "#f0f9ff", fg: "#075985" },
};

function eventChip(type) {
  const meta = EVENT_META[type] || { label: (type || "event").replaceAll("_", " "), bg: "#f1f5f9", fg: "#334155" };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider"
      style={{ background: meta.bg, color: meta.fg }}
      data-testid={`audit-event-chip-${type}`}
    >
      {meta.label}
    </span>
  );
}

/**
 * Audit Log — Global order event feed. Flat, time-descending list of every
 * order-history entry across the system. Backed by `/api/admin/audit/order-events`.
 */
export default function GlobalAuditPanel() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [eventFilter, setEventFilter] = useState("all");
  const [limit, setLimit] = useState(300);
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (debounced) params.search = debounced;
      if (eventFilter && eventFilter !== "all") params.event = eventFilter;
      const { data } = await api.get("/admin/audit/order-events", { params });
      setRows(data?.events || []);
      setTypes(data?.event_types || []);
      setTotal(data?.total || 0);
    } catch (e) {
      toast.error(`Could not load audit events: ${formatApiError(e)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [debounced, eventFilter, limit]);

  const showing = rows.length;

  return (
    <Card className="p-5 rounded-2xl border-border bg-card/80" data-testid="global-audit-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[10px] uppercase tracking-[0.16em] font-semibold mb-2">
            <History className="h-3 w-3" /> Global feed
          </div>
          <h2 className="text-lg font-display font-semibold tracking-tight">Order status changes</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Every status change across every order — who did it, when, and what changed. Filter by event type or search a specific Order # / patient to triage an escalation without opening the database.
          </p>
        </div>
        <div className="text-xs text-muted-foreground shrink-0 text-right">
          <div>
            <span className="font-mono-num font-semibold text-foreground" data-testid="audit-shown-count">{showing}</span>{" "}
            of{" "}
            <span className="font-mono-num font-semibold text-foreground" data-testid="audit-total-count">{total}</span>{" "}
            events
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 mt-1 text-primary hover:underline"
            data-testid="audit-refresh"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-2 md:grid-cols-[1fr_220px_140px] items-end">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Search Order # or Patient</label>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="e.g. SRCI0000265 or Hernandez"
              className="pl-8 h-9"
              data-testid="audit-search"
            />
          </div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Event type</label>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="h-9" data-testid="audit-event-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              {types.map((t) => (
                <SelectItem key={t} value={t}>{(EVENT_META[t]?.label) || t.replaceAll("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Show</label>
          <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v, 10))}>
            <SelectTrigger className="h-9" data-testid="audit-limit"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="100">Last 100</SelectItem>
              <SelectItem value="300">Last 300</SelectItem>
              <SelectItem value="1000">Last 1000</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-border" data-testid="audit-table-wrap">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground p-4" data-testid="audit-empty">
            No events match the current filters.
          </div>
        ) : (
          <div className="max-h-[560px] overflow-y-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="sticky top-0 bg-secondary/70 backdrop-blur border-b border-border">
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="p-2.5 w-[150px]">When</th>
                  <th className="p-2.5 w-[170px]">Event</th>
                  <th className="p-2.5 w-[130px]">Order #</th>
                  <th className="p-2.5">Patient · Pharmacy</th>
                  <th className="p-2.5 w-[150px]">Who</th>
                  <th className="p-2.5">Details</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={`${r.order_id}-${r.at}-${i}`}
                    className="border-t border-border/50 hover:bg-secondary/40"
                    data-testid={`audit-row-${r.order_id}-${i}`}
                  >
                    <td className="p-2.5 text-xs text-muted-foreground whitespace-nowrap font-mono-num">
                      {r.at ? fmtDateTime(r.at) : "—"}
                    </td>
                    <td className="p-2.5">{eventChip(r.type)}</td>
                    <td className="p-2.5 font-mono-num text-xs">
                      {r.order_number || <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-2.5 text-xs">
                      <div className="font-medium text-foreground truncate">{r.customer_name || "—"}</div>
                      {r.pharmacy_label && (
                        <div className="text-muted-foreground text-[11px] truncate">{r.pharmacy_label}</div>
                      )}
                    </td>
                    <td className="p-2.5 text-xs text-foreground truncate">{r.by_name || "system"}</td>
                    <td className="p-2.5 text-xs text-muted-foreground">
                      {r.message || <span className="italic opacity-60">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
